import { Callout, Classes } from '@blueprintjs/core';
import * as React from 'react';
import { Route } from 'react-router-dom';
import { Dialog } from 'vet-bones/components';
import { emptyArray } from 'vet-bones/constants';
import { usePortalConnections } from 'vet-bones/utils/portal';

import { refreshDatabases } from 'src/actions/databases/action-creators/refreshDatabases';
import { ConnectionCard } from 'src/components/cards/ConnectionCard';
import { DatabaseCard } from 'src/components/cards/DatabaseCard';
import { ModelCard } from 'src/components/cards/ModelCard';
import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectModelDialogFooter } from 'src/containers/ProjectModelDialog/ProjectModelDialogFooter';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { useSetupDefaultPortalConnection } from 'src/utils/hooks/useSetupDefaultPortalConnection';

const ProjectModelDialogBody: React.VFC<{
  databaseId: string | null;
  models: string[];
  handleClose: () => void;
  setDatabaseId: (databaseId: string | null) => void;
  setModels: (models: string[]) => void;
}> = ({ databaseId, models, handleClose, setDatabaseId, setModels }) => {
  const dispatch = useReduxDispatch();
  const { projectId } = useRouterParams();
  const isConnected = useReduxSelector(isConnectedSelector);
  const connections = usePortalConnections();

  useSetupDefaultPortalConnection({ connectWithoutToken: true });

  const handleRefreshDatabases = React.useCallback(
    () => dispatch(refreshDatabases()),
    [dispatch]
  );

  React.useEffect(() => {
    if (isConnected) {
      handleRefreshDatabases();
    }
  }, [handleRefreshDatabases, isConnected]);

  React.useEffect(() => {
    setDatabaseId(null);
  }, [setDatabaseId, projectId]);

  return (
    <>
      <div
        className={Classes.DIALOG_BODY}
        data-testid={TEST_IDS.projectModelDialog.body}
      >
        <Callout>
          <div className="sd-dialog-project-model__cards">
            {connections.data && connections.data.length > 1 ? (
              <ConnectionCard />
            ) : null}
            <DatabaseCard
              databaseId={databaseId}
              setDatabaseId={setDatabaseId}
            />
            <ModelCard
              databaseId={databaseId}
              models={models}
              setModels={setModels}
            />
          </div>
        </Callout>
      </div>
      <ProjectModelDialogFooter
        databaseId={databaseId}
        handleClose={handleClose}
        models={models}
      />
    </>
  );
};

export const ProjectModelDialog: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const isOpen = useReduxSelector((state) => state.ui.isProjectModelDialogOpen);

  const [databaseId, setDatabaseId] = React.useState<string | null>(null);
  const [models, setModels] = React.useState<string[]>(emptyArray as any);

  const handleClose = React.useCallback(
    () =>
      dispatch(uiSlice.actions.toggledProjectModelDialog({ isOpen: false })),
    [dispatch]
  );

  React.useEffect(() => {
    if (!isOpen) {
      setDatabaseId(null);
      setModels(emptyArray as any);
    }
  }, [isOpen]);

  return (
    <Dialog
      autoFocus={false}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      className="sd-dialog-project-model"
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      enforceFocus={false}
      isOpen={isOpen}
      lazy={false}
      onClose={handleClose}
      title={copy.components.projectModelDialog.title}
    >
      <Route path={ROUTE.PROJECT}>
        <ProjectModelDialogBody
          databaseId={databaseId}
          handleClose={handleClose}
          models={models}
          setDatabaseId={setDatabaseId}
          setModels={setModels}
        />
      </Route>
    </Dialog>
  );
};
